import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert
} from '@mui/material';
import {
  Public as PublicIcon,
  Water as WaterIcon,
  Flight as FlightIcon,
  Landscape as LandscapeIcon,
  Spa as SpaIcon,
  Science as ScienceIcon,
  Security as SecurityIcon,
  Explore as ExploreIcon
} from '@mui/icons-material';

// Environment icons
const environmentIcons = {
  surface: <LandscapeIcon />,
  hydrosphere: <WaterIcon />,
  atmosphere: <FlightIcon />,
  subterranean: <SpaIcon />,
  space: <PublicIcon />
};

// Planet type icons
const planetTypeIcons = {
  terrestrial: <LandscapeIcon />,
  artificial_habitat: <ScienceIcon />,
  rogue_planet: <ExploreIcon />,
  gas_giant: <PublicIcon />,
  earth_like: <LandscapeIcon />,
  crystal_forests: <ScienceIcon />,
  orbital_fortress: <SecurityIcon />,
  dark_terrestrial: <ExploreIcon />
};

function PlanetDetails({ planet, races, factions }) {
  if (!planet) return <Alert severity="info">Select a planet to view details</Alert>;

  const getEnvironmentIcon = (environment) => {
    return environmentIcons[environment] || <PublicIcon />;
  };

  const getPlanetTypeIcon = (type) => {
    return planetTypeIcons[type] || <PublicIcon />;
  };

  return (
    <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
      {/* Basic Information */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box sx={{ mr: 2 }}>
              {getPlanetTypeIcon(planet.subtype || planet.type)}
            </Box>
            <Box>
              <Typography variant="h6">{planet.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {planet.system_id} • {planet.type} {planet.subtype && `(${planet.subtype})`}
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2"><strong>Gravity:</strong> {planet.gravity}g</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2"><strong>Temperature:</strong> {planet.temperature_c}°C</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2"><strong>Atmosphere:</strong> {planet.atmosphere_type}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {getEnvironmentIcon(planet.environment)}
                <Typography variant="body2" sx={{ ml: 1 }}>
                  <strong>Environment:</strong> {planet.environment}
                </Typography>
              </Box>
            </Grid>
            {planet.biosphere && (
              <Grid item xs={12}>
                <Typography variant="body2"><strong>Biosphere:</strong> {planet.biosphere}</Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Resources */}
      {planet.resources && planet.resources.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>📦 Resources</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {planet.resources.map((resource, index) => (
                <Chip key={index} label={resource} size="small" variant="outlined" />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Compatible Races */}
      {planet.compatible_races && planet.compatible_races.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>👽 Compatible Races</Typography>
            <List dense>
              {planet.compatible_races.map(raceId => {
                const race = races.find(r => r.id === raceId);
                return race ? (
                  <ListItem key={raceId}>
                    <ListItemIcon>
                      {getEnvironmentIcon(race.environment)}
                    </ListItemIcon>
                    <ListItemText 
                      primary={race.name} 
                      secondary={
                        <Box>
                          <Typography variant="body2">{race.description}</Typography>
                          <Chip 
                            label={`Technology: lvl ${race.technology_level}`} 
                            size="small" 
                            variant="outlined"
                            sx={{ mt: 0.5 }}
                          />
                        </Box>
                      } 
                    />
                  </ListItem>
                ) : (
                  <ListItem key={raceId}>
                    <ListItemText primary={raceId} secondary="Race not found in database" />
                  </ListItem>
                );
              })}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Factions */}
      {planet.inhabited_by && planet.inhabited_by.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>🏛️ Factions</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {planet.inhabited_by.map(factionId => {
                const faction = factions.find(f => f.id === factionId);
                return faction ? (
                  <Chip 
                    key={factionId} 
                    label={faction.name} 
                    size="small" 
                    color="secondary" 
                    variant="outlined" 
                  />
                ) : (
                  <Chip key={factionId} label={factionId} size="small" variant="outlined" />
                );
              })}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Settlements */}
      {planet.settlements && planet.settlements.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>🏘️ Settlements</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {planet.settlements.map((settlement, index) => (
                <Chip key={index} label={settlement} size="small" />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Life Goals */}
      {(planet.life_goals_supported || planet.means_of_achievement) && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>🎯 Supported Goals</Typography>
            {planet.life_goals_supported && planet.life_goals_supported.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2"><strong>Life Goals:</strong></Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  {planet.life_goals_supported.map((goal, index) => (
                    <Chip key={index} label={goal} size="small" color="primary" />
                  ))}
                </Box>
              </Box>
            )}
            {planet.means_of_achievement && planet.means_of_achievement.length > 0 && (
              <Box>
                <Typography variant="body2"><strong>Means of Achievement:</strong></Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  {planet.means_of_achievement.map((means, index) => (
                    <Chip key={index} label={means} size="small" variant="outlined" />
                  ))}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

export default PlanetDetails;